import { useEffect, useState } from "react";
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";

const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

/**
 * Convert a stored location to [lng, lat] for react-simple-maps.
 * Real coordinates (lat/lng) are preferred; falls back to the legacy
 * (x %, y %) grid if a row hasn't been migrated yet.
 */
const toCoords = (loc) => {
  if (typeof loc.lng === "number" && typeof loc.lat === "number") {
    return [loc.lng, loc.lat];
  }
  const lng = ((Number(loc.x) || 50) / 100) * 360 - 180;
  const lat = 90 - ((Number(loc.y) || 50) / 100) * 180;
  return [lng, lat];
};

export default function ReachMap({
  locations = [],
  landColor = "#E2E8F0",
  borderColor = "#CBD5E1",
  indiaColor = "#78be27",
  intlColor = "#3da8d8",
  className = "",
}) {
  const [geoData, setGeoData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    fetch(GEO_URL)
      .then((r) => r.json())
      .then((d) => { if (!cancelled) setGeoData(d); })
      .catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, []);

  // Natural aspect ratio (width:height = 1000:500 → 2:1) — preserved via SVG viewBox.
  return (
    <div className={`w-full ${className}`}>
      <ComposableMap
        projection="geoEqualEarth"
        projectionConfig={{ scale: 165, center: [15, 15] }}
        width={1000}
        height={500}
        style={{ width: "100%", height: "auto", display: "block" }}
        aria-hidden
      >
        {geoData && (
          <Geographies geography={geoData}>
            {({ geographies }) =>
              geographies.map((geo) => (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill={landColor}
                  stroke={borderColor}
                  strokeWidth={0.4}
                  style={{
                    default: { outline: "none" },
                    hover: { outline: "none", fill: landColor },
                    pressed: { outline: "none" },
                  }}
                />
              ))
            }
          </Geographies>
        )}

        {locations.map((loc) => {
          const isIndia = loc.region === "India";
          const color = isIndia ? indiaColor : intlColor;
          // India dots are 0.5× the international dots per client's request.
          const r = isIndia ? 2.5 : 5;
          const haloR = isIndia ? 4 : 8;
          return (
            <Marker
              key={loc.id}
              coordinates={toCoords(loc)}
              data-testid={`reach-dot-${loc.id}`}
            >
              <circle r={haloR} fill="none" stroke={color} strokeWidth={1.5} opacity={0.55} />
              <circle r={r} fill={color} />
              <title>{`${loc.city}${loc.country ? ", " + loc.country : ""}`}</title>
            </Marker>
          );
        })}
      </ComposableMap>
    </div>
  );
}
